<template>
  <div class="report-page items-income-report">
    <div class="filter-card filter-card-v3">
      <!-- Top: 4-column primary filter grid -->
      <div class="fcv3-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl">รายการรักษา / ยาและสินค้า</label>
          <b-input-group>
            <multiselect
              class="col p-0 multiselect-h-36px"
              v-model="selectedTxAndDrug"
              :options="combinedTxDrugOptions"
              :limit="5"
              :multiple="true"
              :close-on-select="false"
              group-values="libs"
              group-label="category"
              :group-select="true"
              placeholder="ค้นหารายการรักษา ยา หรือสินค้า"
              :custom-label="customTxDrugsLabel"
              track-by="_track"
              selectLabel
              selectedLabel
              deselectLabel
            >
              <template #option="props">
                <span v-if="props.option.$isLabel">
                  {{ props.option.$groupLabel }}
                </span>
              </template>
              <template #clear="props">
                <div
                  v-if="selectedTxAndDrug.length"
                  class="multiselect__clear"
                  @mousedown.prevent.stop="clearTxDrugFilter(props)"
                ></div>
              </template>
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button
                size="sm"
                class="fi-fav"
                @click="$refs.nameInputModal.openModal()"
              >
                <i class="far fa-star"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <div class="field">
          <label class="field-lbl">ตัวกรองที่บันทึก</label>
          <FavFilter
            ref="favFilter"
            storageName="favouriteFilter"
            :height="36"
            @listClicked="applySelectedTx"
          />
        </div>

        <div class="field">
          <label class="field-lbl">สถานะใบเสร็จ</label>
          <b-dropdown
            ref="receiptStatusDd"
            class="receipt-status-dd"
            menu-class="receipt-status-dd-menu"
            toggle-class="receipt-status-dd-toggle"
            variant="outline-secondary"
            no-caret
            block
            data-testid="receipt-status-select"
          >
            <template #button-content>
              <span
                class="rs-dot"
                :style="{ background: currentReceiptStatus.dotColor }"
              />
              <span class="rs-label">{{ currentReceiptStatus.text }}</span>
              <span class="rs-count">{{ currentReceiptStatus.count }}</span>
              <span class="rs-caret">▾</span>
            </template>
            <b-dropdown-item-button
              v-for="opt in receiptStatusOptWithCounts"
              :key="opt.value"
              :active="opt.value === receiptStatus"
              class="rs-item"
              @click="receiptStatus = opt.value"
            >
              <span class="rs-dot" :style="{ background: opt.dotColor }" />
              <span class="rs-row-label">{{ opt.text }}</span>
              <span class="rs-count">{{ opt.count }}</span>
              <span v-if="opt.value === receiptStatus" class="rs-check">✓</span>
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </div>

      <!-- Bottom: category strip + primary action -->
      <div class="fcv3-strip">
        <span class="fcv3-strip-lbl">หมวดหมู่</span>
        <button
          type="button"
          class="fcv3-all"
          :class="{ 'fcv3-all-active': allCatesSelected }"
          @click="toggleAllCates"
        >
          ทั้งหมด
        </button>
        <span class="fcv3-divider" aria-hidden="true"></span>
        <div class="fcv3-chips">
          <label
            v-for="c in getTreatCateList"
            :key="`t-${c.id}`"
            class="chip fcv3-chip"
            :class="{ 'chip-active': selectedCateIds.includes(c.id) }"
          >
            <input type="checkbox" :value="c.id" v-model="selectedCateIds" />
            <span>{{ c.nameTh }}</span>
          </label>
          <label
            class="chip fcv3-chip fcv3-chip-ortho"
            :class="{ 'chip-active': includeOrthoPkg }"
          >
            <input type="checkbox" v-model="includeOrthoPkg" />
            <span>แพ็กเกจจัดฟัน</span>
          </label>
          <label
            v-for="c in getItemCateList"
            :key="`i-${c.id}`"
            class="chip fcv3-chip fcv3-chip-item"
            :class="{ 'chip-active': selectedItemCateIds.includes(c.id) }"
          >
            <input type="checkbox" :value="c.id" v-model="selectedItemCateIds" />
            <span>{{ c.nameTh }}</span>
          </label>
          <label
            class="chip fcv3-chip fcv3-chip-uncat"
            :class="{ 'chip-active': includeUncategorized }"
          >
            <input type="checkbox" v-model="includeUncategorized" />
            <span>ไม่ระบุ</span>
          </label>
        </div>
        <b-button
          variant="primary"
          class="btn-primary-lg fcv3-cta"
          data-testid="report-search-button"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>
    </div>

    <!-- {{favTxValue}} -->
    <!-- {{selectedTx}} -->
    <div
      id="report-elements"
      ref="fullscreenRoot"
      :class="{ 'my-fullscreen': isFullscreen }"
    >
      <div class="my-fullscreen-bar">
        <span class="title">{{ reportTitle }}</span>
        <div class="meta">
          <span v-if="dateRange[0] && dateRange[1]">
            วันที่: {{ formatDate(dateRange[0]) }} – {{ formatDate(dateRange[1]) }}
          </span>
          <span v-if="branchInfo && branchInfo.name">
            สาขา: {{ branchInfo.name }}
          </span>
        </div>
        <span class="spacer"></span>
        <b-button
          size="sm"
          variant="outline-secondary"
          :title="fullscreenLabel"
          :aria-label="fullscreenLabel"
          @click="toggleFullscreen"
        >
          <i class="fas fa-times"></i>
        </b-button>
      </div>
      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <div class="seg" role="tablist" aria-label="มุมมอง">
            <span class="seg-lbl">มุมมอง:</span>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'financial' }"
              data-testid="view-mode-financial"
              @click="viewMode = 'financial'"
            >
              💰 รายได้ &amp; DF/CF
            </button>
            <button
              type="button"
              class="seg-btn"
              :class="{ active: viewMode === 'payment' }"
              data-testid="view-mode-payment"
              @click="viewMode = 'payment'"
            >
              💳 วิธีชำระ
            </button>
          </div>

          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('branchItemsIncomeTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('branchItemsIncomeTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            size="sm"
            :variant="fullscreenButtonVariant"
            :title="fullscreenLabel"
            :aria-label="fullscreenLabel"
            @click="toggleFullscreen"
          >
            <i class="fas" :class="fullscreenIcon"></i>
          </b-button>
        </div>
      </div>

      <b-table
        id="branchItemsIncomeTable"
        ref="branchItemsIncomeTable"
        class="my-0 sticky-footer report-table items-income-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="creationDt"
        :sort-desc="false"
        v-model="visibleRows"
        :responsive="true"
        caption="Double click เพื่อดูใบเสร็จ/ DF และ CF คำนวนจากการรับชำระของคนไข้ในแต่ละครั้ง"
        :sticky-header="tableHeight"
        :per-page="perPage"
        :current-page="currentPage"
        :tbody-tr-class="rowClassForCancel"
        show-empty
        @row-dblclicked="openReceipt"
      >
        <template #thead-top>
          <b-tr class="grp-band-row">
            <b-th
              :colspan="isExporting ? 10 : 9"
              class="grp-pat grp-band"
            >
              ข้อมูลผู้ป่วย / การรักษา
            </b-th>
            <b-th colspan="5" class="grp-rev grp-band">รายได้</b-th>
            <b-th
              v-if="showFinancial || isExporting"
              :colspan="feeColspan"
              class="grp-fee grp-band"
            >
              ส่วนแบ่ง{{ hasAnyFee ? " &amp; ค่าธรรมเนียม" : "" }}
            </b-th>
            <b-th
              v-if="(showPayment || isExporting) && distinctPaymentMethods.length"
              :colspan="distinctPaymentMethods.length"
              class="grp-pay grp-band"
            >
              วิธีชำระเงินต่อรายการ
            </b-th>
          </b-tr>
        </template>

        <template #head(processingFeeBaht)="data">
          <processing-fee-tooltip
            :label="data.label"
            popover-id="processingFeePopover"
          />
        </template>
        <template #cell(index)="row">{{ row.item.row }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(time)="row">{{ formatTime(row.item.creationDt) }}</template>
        <template #cell(treatType)="row">{{ formatType(row.value) }}</template>
        <template #cell(itemName)="row">
          <span>{{ row.value }}</span>
          <span v-if="row.item.cancelFlag" class="text-danger ml-1">
            (ยกเลิก)
          </span>
        </template>

        <template #custom-foot>
          <!-- Subtotal: ปกติ — only when mixed with cancelled rows -->
          <b-tr v-if="hasMixedFooter" class="text-bold text-right foot-normal">
            <td class="text-left" :colspan="isExporting ? 13 : 12">
              รวม ปกติ {{ normalRows.length }} รายการ
            </td>
            <td>{{ sumOver(normalRows, "paid") }}</td>
            <td>—</td>
            <template v-if="showFinancial || isExporting">
              <td v-if="hasAnyFee">{{ sumOver(normalRows, "processingFeeBaht") }}</td>
              <td>{{ sumOver(normalRows, "paidDf") }}</td>
              <td>{{ sumOver(normalRows, "paidCf") }}</td>
            </template>
            <template v-if="showPayment || isExporting">
              <td
                v-for="m in distinctPaymentMethods"
                :key="`n-${m.id}`"
                data-testid="payment-method-total-normal"
              >
                {{ formatNumber(methodTotalOver(normalRows, m.id)) }}
              </td>
            </template>
          </b-tr>

          <!-- Subtotal: ยกเลิก — only when mixed -->
          <b-tr v-if="hasMixedFooter" class="text-bold text-right row-cancelled">
            <td class="text-left" :colspan="isExporting ? 13 : 12">
              รวม ยกเลิก {{ cancelledRows.length }} รายการ
            </td>
            <td>{{ sumOver(cancelledRows, "paid") }}</td>
            <td>—</td>
            <template v-if="showFinancial || isExporting">
              <td v-if="hasAnyFee">{{ sumOver(cancelledRows, "processingFeeBaht") }}</td>
              <td>{{ sumOver(cancelledRows, "paidDf") }}</td>
              <td>{{ sumOver(cancelledRows, "paidCf") }}</td>
            </template>
            <template v-if="showPayment || isExporting">
              <td
                v-for="m in distinctPaymentMethods"
                :key="`c-${m.id}`"
                data-testid="payment-method-total-cancelled"
              >
                {{ formatNumber(methodTotalOver(cancelledRows, m.id)) }}
              </td>
            </template>
          </b-tr>

          <!-- Grand total — single footer when only one kind, or summary when mixed -->
          <b-tr variant="secondary" class="text-bold text-right">
            <td class="text-left" :colspan="isExporting ? 13 : 12">
              {{ hasMixedFooter ? "รวมทั้งหมด" : "รวม" }} {{ filteredList.length }} รายการ
            </td>
            <td>{{ sum("paid") }}</td>
            <td>—</td>
            <template v-if="showFinancial || isExporting">
              <td v-if="hasAnyFee">{{ sum("processingFeeBaht") }}</td>
              <td>{{ sum("paidDf") }}</td>
              <td>{{ sum("paidCf") }}</td>
            </template>
            <template v-if="showPayment || isExporting">
              <td
                v-for="m in distinctPaymentMethods"
                :key="m.id"
                data-testid="payment-method-total"
              >
                {{ formatNumber(methodTotal(m.id)) }}
              </td>
            </template>
          </b-tr>
        </template>
        <template #empty>
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>
    <NameInput ref="nameInputModal" @submitName="addFav" />
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import ProcessingFeeTooltip from "@/components/tooltip/ProcessingFee";

// import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

import FavFilter from "@/components/FavFilter";
import NameInput from "@/components/modal/NameInput";
import reportFullscreenMixin from "@/mixins/reportFullscreenMixin";

export default {
  name: "BranchItemsIncomeReport",
  mixins: [reportFullscreenMixin],
  components: {
    Dialog,
    Loading,
    FavFilter,
    NameInput,
    ProcessingFeeTooltip,
  },
  data() {
    return {
      isLoading: false,
      viewMode: "financial", // "financial" | "payment"
      isExporting: false,
      reportTitle: "รายงานรายรับ แยกตามรายการรักษา",
      reportNameEn: "Income-Items",

      dateRange: [],
      receiptStatus: "0",
      receiptStatusOpt: [
        { value: "", text: "ทั้งหมด", dotColor: "#9ca3af" },
        { value: "0", text: "ปกติ", dotColor: "#22c55e" },
        { value: "1", text: "ยกเลิก", dotColor: "#ef4444" },
      ],

      //table
      tableBusy: false,
      itemsList: [],
      baseFieldsRaw: [
        { key: "index", label: "", class: "grp-pat" },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing grp-pat",
        },
        {
          key: "time",
          label: "เวลา",
          class: "narrow-spacing grp-pat",
        },
        {
          key: "receiptNo",
          label: "เลขที่ใบเสร็จ",
          class: "narrow-spacing grp-pat",
        },
        { key: "doctorFullName", label: "แพทย์", class: "narrow-spacing grp-pat" },
        { key: "dn", label: "DN.", class: "narrow-spacing grp-pat" },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing grp-pat",
        },
        {
          key: "itemName",
          label: "รายการรักษา",
          class: "narrow-spacing grp-pat",
        },
        { key: "treatType", label: "ประเภท", class: "narrow-spacing grp-pat" },

        { key: "price", label: "ค่ารักษา", class: "text-right grp-rev" },
        { key: "finalDiscount", label: "ส่วนลด", class: "text-right grp-rev" },
        { key: "amount", label: "สุทธิ", class: "text-right grp-rev" },
        { key: "paid", label: "ชำระ", class: "text-right grp-rev" },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger grp-rev",
        },
      ],
      visibleRows: [],
      selectedCateIds: [],
      selectedItemCateIds: [],
      includeOrthoPkg: false,
      includeUncategorized: false,
      selectedTx: [],
      selectedDrug: [],
      favTxValue: [],

      // pagination
      currentPage: 1,
      perPage: 100,

      tableTopPos: null,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getTreatBy: "moduleAppointment/getTreatBy",
      getItemBy: "moduleAppointment/getItemBy",
      getReportDateRange: "moduleReport/getReportDateRange",
      getGroupTxListNoFav: "moduleAppointment/getGroupTxListNoFav", //ย้าย grouping เข้าไปใน appointment module
      getGroupedItemListNoFav: "moduleAppointment/getGroupedItemListNoFav", //ย้าย grouping เข้าไปใน appointment module
      getTreatList: "moduleAppointment/getTreatList",
      getTreatCateList: "moduleAppointment/getTreatCateList",
      getItemList: "moduleAppointment/getItemList",
      getItemCateList: "moduleAppointment/getItemCateList",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    allCatesSelected() {
      const treats = this.getTreatCateList || [];
      const items = this.getItemCateList || [];
      if (treats.length === 0 && items.length === 0) return false;
      return treats.every(c => this.selectedCateIds.includes(c.id))
        && items.every(c => this.selectedItemCateIds.includes(c.id))
        && this.includeOrthoPkg
        && this.includeUncategorized;
    },
    itemCateByDrugItemId() {
      const map = new Map();
      for (const it of this.getItemList || []) {
        if (it.id != null) map.set(it.id, it.cateId);
      }
      return map;
    },
    combinedTxDrugOptions() {
      const tagged = (groups, src) =>
        (groups || []).map(g => ({
          category: g.category,
          libs: (g.libs || []).map(l => ({
            ...l,
            _src: src,
            _track: `${src}-${l.id != null ? l.id : l.nameTh}`,
          })),
        }));
      return [
        ...tagged(this.getGroupTxListNoFav, "tx"),
        ...tagged(this.getGroupedItemListNoFav, "drug"),
      ];
    },
    selectedTxAndDrug: {
      get() {
        return [
          ...this.selectedTx.map(t => ({
            ...t, _src: "tx", _track: `tx-${t.id != null ? t.id : t.nameTh}`,
          })),
          ...this.selectedDrug.map(d => ({
            ...d, _src: "drug", _track: `drug-${d.id != null ? d.id : d.nameTh}`,
          })),
        ];
      },
      set(arr) {
        const stripMeta = ({ _src, _track, ...rest }) => rest;
        this.selectedTx = arr.filter(x => x._src === "tx").map(stripMeta);
        this.selectedDrug = arr.filter(x => x._src === "drug").map(stripMeta);
      },
    },
    statusCounts() {
      const all = this.itemsList.length;
      const cancelled = this.itemsList.filter(i => i.cancelFlag).length;
      return {
        "": all,
        "0": all - cancelled,
        "1": cancelled,
      };
    },
    currentReceiptStatus() {
      const opt = this.receiptStatusOpt.find(o => o.value === this.receiptStatus)
        || this.receiptStatusOpt[0];
      return { ...opt, count: this.statusCounts[opt.value] || 0 };
    },
    receiptStatusOptWithCounts() {
      return this.receiptStatusOpt.map(o => ({
        ...o,
        count: this.statusCounts[o.value] || 0,
      }));
    },
    showFinancial() {
      return this.viewMode === "financial";
    },
    showPayment() {
      return this.viewMode === "payment";
    },
    hasAnyFee() {
      return this.itemsList.some(
        r => parseFloat(r.processingFeeBaht) > 0
      );
    },
    paymentFields() {
      return this.distinctPaymentMethods.map(m => ({
        key: `paymentByMethod.${m.id}`,
        label: m.name,
        class: "text-right grp-pay",
        thClass: "narrow-spacing",
        formatter: v => this.formatNumber(v || 0),
      }));
    },
    fields() {
      // Export-only "สถานะ" column inserted right after `treatType` so it
      // sits inside the patient/treatment band. Hidden on screen.
      const base = this.isExporting
        ? [
            ...this.baseFieldsRaw.slice(0, 8),
            {
              key: "_cancelStatusLabel",
              label: "สถานะ",
              class: "narrow-spacing grp-pat",
            },
            ...this.baseFieldsRaw.slice(8),
          ]
        : this.baseFieldsRaw;

      if (this.viewMode === "payment" && !this.isExporting) {
        return [...base, ...this.paymentFields];
      }

      // financial mode (and exports)
      const feeFields = [];
      if (this.hasAnyFee) {
        feeFields.push({
          key: "processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right grp-fee",
        });
      }
      feeFields.push(
        { key: "paidDf", label: "DF", class: "text-right grp-fee" },
        { key: "paidCf", label: "CF", class: "text-right grp-fee" }
      );

      if (this.isExporting) {
        return [...base, ...feeFields, ...this.paymentFields];
      }
      return [...base, ...feeFields];
    },
    feeColspan() {
      if (this.viewMode !== "financial" && !this.isExporting) return 0;
      return this.hasAnyFee ? 3 : 2;
    },
    distinctPaymentMethods() {
      const map = new Map();
      for (const item of this.itemsList) {
        for (const a of item.paymentAllocations || []) {
          if (!map.has(a.id)) map.set(a.id, { id: a.id, name: a.name });
        }
      }
      return [...map.values()].sort((a, b) => a.id - b.id);
    },
    totalsByMethod() {
      return this.distinctPaymentMethods
        .map(m => ({
          id: m.id,
          name: m.name,
          amount: this.filteredList.reduce((acc, item) => {
            const hit = (item.paymentAllocations || []).find(
              a => a.id === m.id
            );
            return acc + (hit ? parseFloat(hit.amount) : 0);
          }, 0),
        }))
        .filter(t => t.amount > 0);
    },
    filterItemId() {
      return this.selectedTx.map(i => i.id);
    },
    filterDrugId() {
      return this.selectedDrug.map(i => i.id);
    },

    treatCateByItemId() {
      const map = new Map();
      for (const t of this.getTreatList || []) {
        if (t.id != null) map.set(t.id, t.cateId);
      }
      return map;
    },
    filteredList() {
      let filteredData = this.itemsList;
      if (this.receiptStatus === "0") {
        filteredData = filteredData.filter(i => !i.cancelFlag);
      } else if (this.receiptStatus === "1") {
        filteredData = filteredData.filter(i => i.cancelFlag);
      }
      const hasTxFilter = this.filterItemId.length > 0;
      const hasDrugFilter = this.filterDrugId.length > 0;
      if (hasTxFilter || hasDrugFilter) {
        const txSet = new Set(this.filterItemId);
        const drugSet = new Set(this.filterDrugId);
        filteredData = filteredData.filter(i => {
          if (hasTxFilter && txSet.has(parseInt(i.txItemId))) return true;
          if (hasDrugFilter && drugSet.has(parseInt(i.drugItemId))) return true;
          return false;
        });
      }
      const hasTreat = this.selectedCateIds.length > 0;
      const hasOrtho = this.includeOrthoPkg;
      const hasItem = this.selectedItemCateIds.length > 0;
      const hasUncat = this.includeUncategorized;
      if (hasTreat || hasOrtho || hasItem || hasUncat) {
        const allowedTreat = new Set(this.selectedCateIds);
        const allowedItem = new Set(this.selectedItemCateIds);
        filteredData = filteredData.filter(i => {
          if (this.isUncategorizedRow(i)) return hasUncat;
          if (i.treatType === 2) return hasOrtho;
          if (i.treatType === 3) {
            return hasItem
              && allowedItem.has(this.itemCateByDrugItemId.get(parseInt(i.drugItemId)));
          }
          return hasTreat
            && allowedTreat.has(this.treatCateByItemId.get(parseInt(i.txItemId)));
        });
      }
      return filteredData
        .slice(0)
        .sort(
          (a, b) =>
            moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf()
        )
        .map((i, index) => {
          const paymentByMethod = (i.paymentAllocations || []).reduce(
            (acc, a) => {
              acc[a.id] = parseFloat(a.amount) || 0;
              return acc;
            },
            {}
          );
          const _cancelStatusLabel = i.cancelFlag ? "ยกเลิก" : "ปกติ";
          return { ...i, row: index + 1, paymentByMethod, _cancelStatusLabel };
        });
    },
    startDt() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    endDt() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
    normalRows() {
      return this.filteredList.filter(r => !r.cancelFlag);
    },
    cancelledRows() {
      return this.filteredList.filter(r => r.cancelFlag);
    },
    hasMixedFooter() {
      return this.normalRows.length > 0 && this.cancelledRows.length > 0;
    },
    tableHeight() {
      if (this.isFullscreen) return "100%";
      return `calc(100vh - ${this.tableTopPos + 40}px)`;
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportBranchReceiptItem: "moduleReport/fetchReportBranchReceiptItem",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {},
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatTime(date) {
      return date ? moment(date).locale("th").format("HH:mm") : "";
    },

    resetDateRange() {
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
    },
    formatType(code) {
      switch (code) {
        case 1:
          return "รักษาทั่วไป";
        case 2:
          return "แพ็กเกจจัดฟัน";
        case 3:
          return "ยา และอุปกรณ์";
      }
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sumOver(rows, key) {
      const num = (rows || []).reduce(
        (accum, item) => accum + (parseFloat(item[key]) || 0),
        0,
      );
      return Math.round(num * 100) / 100;
    },
    sum(key) {
      return this.sumOver(this.filteredList, key);
    },
    methodTotalOver(rows, id) {
      return (rows || []).reduce(
        (acc, item) => acc + ((item.paymentByMethod && item.paymentByMethod[id]) || 0),
        0,
      );
    },
    methodTotal(id) {
      return this.methodTotalOver(this.filteredList, id);
    },
    rowClassForCancel(item) {
      return item && item.cancelFlag ? "row-cancelled" : "";
    },
    formatNumber(value) {
      const num = Number(value) || 0;
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    generatePDf() {
      eventBus.$emit(
        "generatePDF",
        this.$refs["branchItemsIncomeTable"].$el,
        this.$refs.header,
        this.$refs.footer
      );
    },
    sendEmail() {
      //รอ api
    },
    fetchReport() {
      if (this.startDt === "" || this.endDt === "") return;

      this.setStateReportDateRange(this.dateRange);
      this.isLoading = true;
      this.fetchReportBranchReceiptItem({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        startDt: this.startDt,
        endDt: this.endDt,
        receiptStatus: "",
      })
        .then(res => {
          this.itemsList = res.data.report;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async excelExport(id) {
      this.isExporting = true;
      this.perPage = null;
      try {
        await this.$nextTick();

        exportTableToExcel({
          tableId: id,
          reportTitle: this.reportTitle,
          reportNameEn: this.reportNameEn,
          dateFrom: this.dateRange[0],
          dateTo: this.dateRange[1],
          branchName: this.branchInfo && this.branchInfo.name,
          clinicName: this.clinicInfo && this.clinicInfo.name,
        });
      } finally {
        this.perPage = 100;
        this.isExporting = false;
      }
    },
    async pdfExport(id) {
      this.isExporting = true;
      this.perPage = null;
      try {
        await this.$nextTick();

        exportReportToPdf({
          tableId: id,
          reportTitle: this.reportTitle,
          reportNameEn: this.reportNameEn,
          dateFrom: this.dateRange[0],
          dateTo: this.dateRange[1],
          branchName: this.branchInfo && this.branchInfo.name,
          clinicName: this.clinicInfo && this.clinicInfo.name,
        });
      } finally {
        this.perPage = 100;
        this.isExporting = false;
      }
    },

    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openReceipt(data) {
      eventBus.$emit("openReceipt", data);
    },
    customTxDrugsLabel(option) {
      if (option.nameTh == option.nameEn) {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}`;
      } else {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}${
          option.nameEn ? " / " + option.nameEn : ""
        }`;
      }
    },
    toggleAllCates() {
      if (this.allCatesSelected) {
        this.selectedCateIds = [];
        this.selectedItemCateIds = [];
        this.includeOrthoPkg = false;
        this.includeUncategorized = false;
      } else {
        this.selectedCateIds = (this.getTreatCateList || []).map(c => c.id);
        this.selectedItemCateIds = (this.getItemCateList || []).map(c => c.id);
        this.includeOrthoPkg = true;
        this.includeUncategorized = true;
      }
    },
    isUncategorizedRow(row) {
      if (row.treatType === 2) return false;
      if (row.treatType === 3) {
        return this.itemCateByDrugItemId.get(parseInt(row.drugItemId)) == null;
      }
      return this.treatCateByItemId.get(parseInt(row.txItemId)) == null;
    },
    clearTxDrugFilter() {
      this.selectedTx = [];
      this.selectedDrug = [];
    },
    applySelectedTx({ selectedTx, selectedDrug }) {
      this.selectedTx = selectedTx.flat();
      this.selectedDrug = selectedDrug.flat();
    },
    addFav(e) {
      let data = {
        text: e,
        selectedTx: this.selectedTx,
        selectedDrug: this.selectedDrug,
      };
      this.$refs.favFilter.addItem(data);
    },
  },
  async mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
    await this.$nextTick();
    const tableElement = this.$refs.branchItemsIncomeTable?.$el ||
                         document.querySelector("#branchItemsIncomeTable");
    this.tableTopPos = tableElement
      ? tableElement.getBoundingClientRect().top
      : 200; // fallback value if element not found

    eventBus.$on("afterCancelReceipt", e => {
      this.fetchReport();
    });
  },
  updated() {},
};
</script>

<style>
/* Items-Income-specific: grouped header bands (band row + per-group cell tints).
   Generic chrome (filter-card, toolbar, chips, table typography, sticky-header
   override) lives in src/theme/reports.scss. */
.multiselect-h-38px .multiselect__tags {
  min-height: 38px;
}
.multiselect-h-31px .multiselect__tags {
  min-height: 31px;
}

.items-income-table thead .grp-pat {
  background: #fbfcfd !important;
}
.items-income-table thead .grp-rev {
  background: #f6f9fc !important;
}
.items-income-table thead .grp-fee {
  background: #fbf6ee !important;
}
.items-income-table thead .grp-pay {
  background: #f3faf6 !important;
}
.items-income-table tbody td.grp-pat {
  background: #fdfeff;
}
.items-income-table tbody td.grp-rev {
  background: #f9fbfe;
}
.items-income-table tbody td.grp-fee {
  background: #fdfaf3;
}
.items-income-table tbody td.grp-pay {
  background: #f9fdfb;
}

/* group band row typography */
.items-income-table thead .grp-band-row .grp-band {
  text-align: center;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a5568;
  padding: 4px 8px;
  border-bottom: 1px solid #e5e9ef;
}
.items-income-report .items-income-table thead .grp-band-row .grp-band {
  font-size: 9.5px;
  padding: 2px 6px;
}

.items-income-report .pmt-chip {
  font-size: 10.5px;
  padding: 1px 6px;
}

.items-income-report .fcv3-primary {
  grid-template-columns:
    200px minmax(0, 1fr) minmax(0, 1fr) 220px;
}
@media (max-width: 991px) {
  .items-income-report .fcv3-primary {
    grid-template-columns: 1fr;
  }
}

/* receipt-status dropdown — colored dot + count, V6d direction */
.items-income-report .receipt-status-dd .dropdown-toggle.receipt-status-dd-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #ced4da;
  font-size: 13px;
  font-weight: 400;
  text-align: left;
}
.items-income-report .receipt-status-dd .dropdown-toggle.receipt-status-dd-toggle:hover,
.items-income-report .receipt-status-dd .dropdown-toggle.receipt-status-dd-toggle:focus,
.items-income-report .receipt-status-dd.show .dropdown-toggle.receipt-status-dd-toggle {
  background: #fff;
  color: #1a1a1a;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.18);
}
.items-income-report .rs-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex: none;
}
.items-income-report .rs-label,
.items-income-report .rs-row-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.items-income-report .rs-count {
  font-size: 11px;
  color: #8a8a8a;
  font-variant-numeric: tabular-nums;
}
.items-income-report .rs-caret {
  font-size: 11px;
  color: #8a8a8a;
  margin-left: 2px;
}
.items-income-report .receipt-status-dd-menu {
  min-width: 100%;
  padding: 4px;
}
.items-income-report .rs-item .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 4px;
  font-size: 13px;
}
.items-income-report .rs-item .dropdown-item.active,
.items-income-report .rs-item .dropdown-item:active {
  background: #fff4d6;
  color: #1a1a1a;
}
.items-income-report .rs-check {
  color: #ff6b35;
  font-weight: 700;
}

/* Equalize all primary-filter inputs at 36px outer height */
.items-income-report .fcv3-primary .field {
  align-self: stretch;
}
/* Date picker */
.items-income-report .fcv3-primary .mx-datepicker {
  width: 100%;
  height: 36px;
}
.items-income-report .fcv3-primary .mx-datepicker .mx-input {
  height: 36px !important;
  box-sizing: border-box;
}
/* Multiselect — exact 36px when nothing is selected. When items are
   selected the multiselect grows naturally beyond 36px (active state). */
.items-income-report .fcv3-primary .multiselect,
.items-income-report .fcv3-primary .multiselect__tags {
  box-sizing: border-box;
}
.items-income-report .fcv3-primary .multiselect:not(.multiselect--active) {
  min-height: 36px;
  height: 36px;
}
.items-income-report .fcv3-primary
  .multiselect:not(.multiselect--active) .multiselect__tags {
  min-height: 36px;
  height: 36px;
  padding: 0 40px 0 8px;
  display: flex;
  align-items: center;
}
.items-income-report .fcv3-primary .multiselect__select {
  height: 34px;
  top: 0;
}
.items-income-report .fcv3-primary .multiselect__placeholder,
.items-income-report .fcv3-primary .multiselect__single {
  margin: 0;
  padding: 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
}
.items-income-report .fcv3-primary .multiselect__input {
  margin: 0;
  padding: 0;
  min-height: 22px;
  line-height: 22px;
  background: transparent;
}
.items-income-report .fcv3-primary .input-group {
  align-items: center;
  height: 36px;
  flex-wrap: nowrap;
}
.items-income-report .fcv3-primary .input-group .multiselect__tags {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right-width: 0;
}
.items-income-report .fcv3-primary .input-group .fi-fav,
.items-income-report .fcv3-primary .input-group .input-group-append .btn {
  height: 36px !important;
  min-height: 36px;
  max-height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.items-income-report .fcv3-primary .input-group-append {
  height: 36px;
  display: flex;
  align-items: center;
}
/* FavFilter — collapse the disabled placeholder list-group-item to 36px so
   the field height matches the other controls in the row. */
.items-income-report .fcv3-primary .field > .mb-1 {
  margin-bottom: 0 !important;
  height: 36px;
}
.items-income-report .fcv3-primary .field .list-group {
  height: 36px;
}
.items-income-report .fcv3-primary .field .list-group-item.p-1 {
  height: 36px;
  display: flex;
  align-items: center;
  padding: 0 12px !important;
  font-size: 13px;
  line-height: 1.2;
}

/* Normal-subtotal footer row — opaque so sticky-footer doesn't blend with
   scrolled body rows behind it */
.items-income-report tfoot tr.foot-normal td {
  background: #ffffff !important;
  border-top: 1px solid #e5e9ef;
}

/* Cancelled rows — soft red tint that overrides per-column-group bands */
.items-income-report .row-cancelled td,
.items-income-report .row-cancelled td.grp-pat {
  background: #fef2f2 !important;
}
.items-income-report .row-cancelled td.grp-rev,
.items-income-report .row-cancelled td.grp-fee,
.items-income-report .row-cancelled td.grp-pay {
  background: #fee2e2 !important;
}
.items-income-report .row-cancelled td:first-child {
  box-shadow: inset 3px 0 0 #ef4444;
}
.items-income-report tfoot tr.row-cancelled td:first-child {
  box-shadow: inset 3px 0 0 #ef4444;
}
</style>
